import { useState } from 'react';
import type { QueryKey } from '@tanstack/react-query';

/** The slice of a TanStack Query result this hook reads (works for `useQuery` + `useInfiniteQuery`). */
export interface QueryFreshnessState {
  isPending: boolean;
  isStale: boolean;
  isFetching: boolean;
  isFetchedAfterMount: boolean;
  /** Failed attempts of the current fetch (resets on success) — ends the gate on the first failure. */
  failureCount: number;
}

/**
 * "Skeleton unless this entity's data is cached AND fresh" — the detail-panel rule from
 * `docs/code-standards.md` → "Loading states".
 *
 * TanStack renders cached data instantly even when it is stale and refetches in the background, so
 * reopening a panel paints outdated data that swaps ~1s later. This returns `true` while:
 * - nothing is cached yet (`isPending`), or
 * - the data was stale when `entityKey` was opened AND the refetch triggered by that open is still
 *   running.
 *
 * Fresh cache (e.g. seeded from a mutation response, or read seconds ago) renders straight away, and
 * a later background refetch of an already-shown entity (realtime / mutation invalidation) stays
 * silent — only the "opened on stale data" case is gated. The first failed attempt of that refetch
 * already falls back to the cached copy (no waiting out the default retry back-off of ~7s behind a
 * skeleton); retries continue in the background and update in place if one succeeds.
 *
 * `entityKey` is the id of the entity being shown (group id, appointment id…); switching it re-arms
 * the check so entity B never renders A's leftovers or its own outdated copy.
 */
export function useSkeletonUntilFresh(query: QueryFreshnessState, entityKey: string | null): boolean {
  const staleOnOpen = query.isPending || query.isStale;
  const [opened, setOpened] = useState({ entityKey, staleOnOpen });

  // Derive-from-props pattern: re-capture freshness in the same render the entity switches.
  let current = opened;
  if (opened.entityKey !== entityKey) {
    current = { entityKey, staleOnOpen };
    setOpened(current);
  }

  if (query.isPending) return true;
  return (
    current.staleOnOpen &&
    query.isFetching &&
    !query.isFetchedAfterMount &&
    query.failureCount === 0
  );
}

/**
 * `placeholderData` for a list that is being refined (search / filter / range change): keeps showing
 * the previous rows while the new key loads — the caller dims them via `query.isPlaceholderData` —
 * but ONLY when the previous query belongs to the same scope. `isSameScope(previousKey)` must reject
 * keys from another salon / privacy scope / entity, so a salon switch shows a skeleton instead of
 * the other salon's rows. Plain `keepPreviousData` has no such guard.
 */
export function keepPreviousDataWhen(isSameScope: (previousKey: QueryKey) => boolean) {
  return <TData>(previousData: TData | undefined, previousQuery?: { queryKey: QueryKey }) =>
    previousQuery && isSameScope(previousQuery.queryKey) ? previousData : undefined;
}
